import React, { useState, useEffect } from "react";
import * as api from "../../services/api";
import { exportCategoriesToExcel, importCategoriesFromExcel } from "../../utils/excelUtils";
import { convertGoogleDriveUrlForDisplay, getPlaceholderImage } from "../../utils/imageUtils";
import "./CategoriesManagement.css";

export default function CategoriesManagement() {
  const [categories, setCategories] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showCategoryModal, setShowCategoryModal] = useState(false);
  const [showSubcategoryModal, setShowSubcategoryModal] = useState(false);
  const [editingCategory, setEditingCategory] = useState(null);
  const [selectedCategory, setSelectedCategory] = useState(null);
  const [error, setError] = useState("");
  const [formData, setFormData] = useState({
    name: "",
    slug: "",
    image: ""
  });
  const [uploadingImage, setUploadingImage] = useState(false);
  const [imageUploadProgress, setImageUploadProgress] = useState('');
  const [subcategoryForm, setSubcategoryForm] = useState({
    name: "",
    slug: ""
  });
  const [slugManuallyEdited, setSlugManuallyEdited] = useState(false);
  const [subcategorySlugManuallyEdited, setSubcategorySlugManuallyEdited] = useState(false);
  const [uploading, setUploading] = useState(false);
  const [searchQuery, setSearchQuery] = useState("");
  const [filterHasSubcategories, setFilterHasSubcategories] = useState("all");
  const [filterHasImage, setFilterHasImage] = useState("all");

  useEffect(() => {
    loadCategories();
  }, []);

  const loadCategories = async () => {
    try {
      setLoading(true);
      const categoriesData = await api.getCategories();
      console.log('=== ADMIN: CATEGORIES LOADED ===');
      categoriesData.forEach(cat => {
        console.log(`Category: ${cat.name}`, {
          _id: cat._id,
          hasImage: !!cat.image,
          imageType: cat.image ? typeof cat.image : 'none',
          imageValue: cat.image ? (cat.image.substring ? cat.image.substring(0, 100) : String(cat.image).substring(0, 100)) : 'none'
        });
      });
      setCategories(categoriesData);
    } catch (error) {
      console.error('Error loading categories:', error);
      setError('Failed to load categories');
    } finally {
      setLoading(false);
    }
  };

  const generateSlug = (text) => {
    return text
      .toLowerCase()
      .trim()
      .replace(/[^a-z0-9]+/g, '-')
      .replace(/^-+|-+$/g, '');
  };

  const handleInputChange = (e) => {
    const { name, value } = e.target;
    
    if (name === 'name') {
      // Auto-generate slug from name if slug hasn't been manually edited
      setFormData(prev => ({
        ...prev,
        name: value,
        slug: slugManuallyEdited ? prev.slug : generateSlug(value)
      }));
    } else if (name === 'slug') {
      // Mark slug as manually edited if user changes it
      setSlugManuallyEdited(true);
      setFormData(prev => ({
        ...prev,
        slug: value
      }));
    } else {
      setFormData(prev => ({
        ...prev,
        [name]: value
      }));
    }
  };

  const handleSubcategoryInputChange = (e) => {
    const { name, value } = e.target;
    
    if (name === 'name') {
      // Auto-generate slug from name if slug hasn't been manually edited
      setSubcategoryForm(prev => ({
        ...prev,
        name: value,
        slug: subcategorySlugManuallyEdited ? prev.slug : generateSlug(value)
      }));
    } else if (name === 'slug') {
      // Mark slug as manually edited if user changes it
      setSubcategorySlugManuallyEdited(true);
      setSubcategoryForm(prev => ({
        ...prev,
        slug: value
      }));
    } else {
      setSubcategoryForm(prev => ({
        ...prev,
        [name]: value
      }));
    }
  };

  const handleImageUpload = async (e) => {
    const file = e.target.files[0];
    if (!file) return;

    // Validate file type
    if (!file.type.startsWith('image/')) {
      setError('Only image files are allowed');
      setTimeout(() => setError(''), 5000);
      e.target.value = '';
      return;
    }

    setUploadingImage(true);
    setImageUploadProgress('Uploading image...');
    setError('');

    try {
      console.log('=== UPLOADING IMAGE ===');
      console.log('File:', {
        name: file.name,
        size: file.size,
        type: file.type
      });
      
      const result = await api.uploadImage(file);
      console.log('Upload result:', result);
      
      if (result && result.url) {
        console.log('Setting image in formData:', {
          urlLength: result.url.length,
          urlPreview: result.url.substring(0, 100),
          urlType: result.url.startsWith('data:') ? 'base64' : 'url'
        });
        
        setFormData(prev => {
          const updated = {
            ...prev,
            image: result.url
          };
          console.log('Updated formData:', {
            ...updated,
            imagePreview: updated.image ? updated.image.substring(0, 100) : 'none'
          });
          return updated;
        });
        
        setImageUploadProgress('✅ Image uploaded successfully!');
        setTimeout(() => setImageUploadProgress(''), 3000);
      } else {
        console.error('Invalid upload result:', result);
        throw new Error('Invalid response from server');
      }
    } catch (error) {
      console.error('Image upload error:', error);
      console.error('Error details:', {
        message: error.message,
        response: error.response?.data,
        stack: error.stack
      });
      setError(error.response?.data?.message || 'Failed to upload image. Please try again.');
      setTimeout(() => setError(''), 5000);
    } finally {
      setUploadingImage(false);
      e.target.value = ''; // Reset file input
    }
  };

  const handleCreateCategory = async (e) => {
    e.preventDefault();
    setError("");

    if (!formData.name) {
      setError("Category name is required");
      return;
    }

    try {
      console.log('=== CREATE CATEGORY FORM SUBMITTED ===');
      console.log('Current formData:', {
        ...formData,
        imagePreview: formData.image ? formData.image.substring(0, 100) : 'none',
        imageLength: formData.image ? formData.image.length : 0,
        hasImage: !!formData.image
      });
      
      const categoryData = {
        name: formData.name,
        slug: formData.slug || generateSlug(formData.name),
        subcategories: []
      };
      
      // Only include image if it has a value
      if (formData.image && typeof formData.image === 'string' && formData.image.trim().length > 0) {
        categoryData.image = formData.image.trim();
        console.log('✅ Image included in categoryData');
      } else {
        categoryData.image = "";
        console.log('⚠️ Image is empty or invalid, setting to empty string');
      }
      
      console.log('Creating category with data:', {
        categoryData,
        imageLength: categoryData.image ? categoryData.image.length : 0,
        imagePreview: categoryData.image ? categoryData.image.substring(0, 100) : 'none'
      });

      const createdCategory = await api.createCategory(categoryData);
      console.log('Category created, response:', createdCategory);
      setFormData({ name: "", slug: "", image: "" });
      setSlugManuallyEdited(false);
      setImageUploadProgress('');
      setShowCategoryModal(false);
      loadCategories();
    } catch (error) {
      console.error('Error creating category:', error);
      setError(error.response?.data?.message || 'Failed to create category');
    }
  };

  const handleAddSubcategory = async (e) => {
    e.preventDefault();
    setError("");

    if (!subcategoryForm.name || !selectedCategory) {
      setError("Subcategory name and category are required");
      return;
    }

    try {
      const subcategoryData = {
        name: subcategoryForm.name,
        slug: subcategoryForm.slug || generateSlug(subcategoryForm.name)
      };

      const updatedSubcategories = [
        ...(selectedCategory.subcategories || []),
        subcategoryData
      ];

      await api.updateCategory(selectedCategory._id, {
        subcategories: updatedSubcategories
      });

      setSubcategoryForm({ name: "", slug: "" });
      setSubcategorySlugManuallyEdited(false);
      setSelectedCategory(null);
      setShowSubcategoryModal(false);
      loadCategories();
    } catch (error) {
      console.error('Error adding subcategory:', error);
      setError(error.response?.data?.message || 'Failed to add subcategory');
    }
  };

  const handleDeleteCategory = async (categoryId) => {
    if (!window.confirm('Are you sure you want to delete this category? This will also delete all its subcategories.')) {
      return;
    }

    try {
      await api.deleteCategory(categoryId);
      loadCategories();
    } catch (error) {
      console.error('Error deleting category:', error);
      alert('Failed to delete category');
    }
  };

  const handleDeleteSubcategory = async (categoryId, subcategoryIndex) => {
    if (!window.confirm('Are you sure you want to delete this subcategory?')) {
      return;
    }

    try {
      const category = categories.find(cat => cat._id === categoryId);
      if (!category) return;

      const updatedSubcategories = category.subcategories.filter((_, index) => index !== subcategoryIndex);
      await api.updateCategory(categoryId, {
        subcategories: updatedSubcategories
      });

      loadCategories();
    } catch (error) {
      console.error('Error deleting subcategory:', error);
      alert('Failed to delete subcategory');
    }
  };

  const handleEditCategory = (category) => {
    setEditingCategory(category);
    setFormData({
      name: category.name,
      slug: category.slug,
      image: category.image || ""
    });
    setSlugManuallyEdited(false);
    setImageUploadProgress('');
    setShowCategoryModal(true);
  };

  const handleDownloadExcel = () => {
    try {
      exportCategoriesToExcel(categories);
      alert('Categories exported to Excel successfully!');
    } catch (error) {
      console.error('Error exporting categories:', error);
      alert('Failed to export categories to Excel');
    }
  };

  const handleUploadExcel = async (event) => {
    const file = event.target.files[0];
    if (!file) return;

    if (!file.name.endsWith('.xlsx') && !file.name.endsWith('.xls')) {
      alert('Please upload a valid Excel file (.xlsx or .xls)');
      return;
    }

    setUploading(true);
    setError("");

    try {
      const importedCategories = await importCategoriesFromExcel(file);
      
      if (importedCategories.length === 0) {
        alert('No valid categories found in the Excel file');
        setUploading(false);
        return;
      }

      // Ask for confirmation
      const confirmed = window.confirm(
        `Found ${importedCategories.length} categories. Do you want to import them?`
      );

      if (!confirmed) {
        setUploading(false);
        return;
      }

      // Import categories one by one
      let successCount = 0;
      let errorCount = 0;

      for (const category of importedCategories) {
        try {
          await api.createCategory(category);
          successCount++;
        } catch (error) {
          console.error('Error importing category:', error);
          errorCount++;
        }
      }

      alert(`Import completed! ${successCount} categories imported successfully, ${errorCount} failed.`);
      loadCategories();
    } catch (error) {
      console.error('Error importing categories:', error);
      setError(error.message || 'Failed to import categories from Excel');
    } finally {
      setUploading(false);
      // Reset file input
      event.target.value = '';
    }
  };

  const handleUpdateCategory = async (e) => {
    e.preventDefault();
    setError("");

    if (!formData.name || !editingCategory) {
      setError("Category name is required");
      return;
    }

    try {
      const updateData = {
        name: formData.name,
        slug: formData.slug || generateSlug(formData.name)
      };
      
      // Only include image if it has a value
      if (formData.image && typeof formData.image === 'string' && formData.image.trim().length > 0) {
        updateData.image = formData.image.trim();
      } else {
        // If image is empty, explicitly set to empty string to clear it
        updateData.image = "";
      }
      
      console.log('Updating category with data:', {
        id: editingCategory._id,
        updateData,
        imageLength: updateData.image ? updateData.image.length : 0
      });

      const updatedCategory = await api.updateCategory(editingCategory._id, updateData);
      console.log('Category updated, response:', updatedCategory);

      setFormData({ name: "", slug: "", image: "" });
      setSlugManuallyEdited(false);
      setImageUploadProgress('');
      setEditingCategory(null);
      setShowCategoryModal(false);
      loadCategories();
    } catch (error) {
      console.error('Error updating category:', error);
      setError(error.response?.data?.message || 'Failed to update category');
    }
  };

  // Filter and search categories
  const filteredCategories = categories.filter(category => {
    // Search filter
    if (searchQuery) {
      const query = searchQuery.toLowerCase();
      const nameMatch = (category.name || "").toLowerCase().includes(query);
      const slugMatch = (category.slug || "").toLowerCase().includes(query);
      const subcategoryMatch = category.subcategories?.some(sub => 
        (sub.name || "").toLowerCase().includes(query) || 
        (sub.slug || "").toLowerCase().includes(query)
      );
      if (!nameMatch && !slugMatch && !subcategoryMatch) {
        return false;
      }
    }

    // Subcategories filter
    if (filterHasSubcategories === "yes" && (!category.subcategories || category.subcategories.length === 0)) {
      return false;
    }
    if (filterHasSubcategories === "no" && category.subcategories && category.subcategories.length > 0) {
      return false;
    }

    // Image filter
    if (filterHasImage === "yes" && (!category.image || category.image.trim().length === 0)) {
      return false;
    }
    if (filterHasImage === "no" && category.image && category.image.trim().length > 0) {
      return false;
    }

    return true;
  });

  const clearFilters = () => {
    setSearchQuery("");
    setFilterHasSubcategories("all");
    setFilterHasImage("all");
  };

  if (loading) {
    return <div className="categories-loading">Loading categories...</div>;
  }

  return (
    <div className="categories-management">
      <div className="categories-header">
        <h1>Categories Management</h1>
        <div className="categories-actions">
          <label className="btn-upload" htmlFor="category-upload">
            {uploading ? '⏳ Uploading...' : '📥 Upload Excel'}
            <input
              id="category-upload"
              type="file"
              accept=".xlsx,.xls"
              onChange={handleUploadExcel}
              style={{ display: 'none' }}
              disabled={uploading}
            />
          </label>
          <button 
            className="btn-download"
            onClick={handleDownloadExcel}
            disabled={categories.length === 0}
          >
            📥 Download Excel
          </button>
          <button 
            className="btn-secondary"
            onClick={() => {
              setSelectedCategory(null);
              setSubcategoryForm({ name: "", slug: "" });
              setSubcategorySlugManuallyEdited(false);
              setShowSubcategoryModal(true);
            }}
          >
            ➕ Add Subcategory
          </button>
          <button 
            className="btn-primary"
            onClick={() => {
              setEditingCategory(null);
              setFormData({ name: "", slug: "", image: "" });
              setSlugManuallyEdited(false);
              setImageUploadProgress('');
              setShowCategoryModal(true);
            }}
          >
            ➕ Add Category
          </button>
        </div>
      </div>

      {error && <div className="error-message">{error}</div>}

      {/* Search and Filter Section */}
      <div className="search-filter-section">
        <div className="search-bar">
          <input
            type="text"
            placeholder="🔍 Search by category name, slug, or subcategory..."
            value={searchQuery}
            onChange={(e) => setSearchQuery(e.target.value)}
            className="search-input"
          />
        </div>
        <div className="filters-row">
          <select
            value={filterHasSubcategories}
            onChange={(e) => setFilterHasSubcategories(e.target.value)}
            className="filter-select"
          >
            <option value="all">All Categories</option>
            <option value="yes">With Subcategories</option>
            <option value="no">Without Subcategories</option>
          </select>
          <select
            value={filterHasImage}
            onChange={(e) => setFilterHasImage(e.target.value)}
            className="filter-select"
          >
            <option value="all">All Categories</option>
            <option value="yes">With Image</option>
            <option value="no">Without Image</option>
          </select>
          <button
            onClick={clearFilters}
            className="btn-clear-filters"
            title="Clear all filters"
          >
            🗑️ Clear Filters
          </button>
        </div>
        <div className="results-count">
          Showing {filteredCategories.length} of {categories.length} categories
        </div>
      </div>

      <div className="categories-list">
        {filteredCategories.length === 0 ? (
          <div className="empty-state">
            <p>
              {categories.length === 0 
                ? "No categories found. Add your first category!" 
                : "No categories match your search/filter criteria."}
            </p>
          </div>
        ) : (
          filteredCategories.map((category) => (
            <div key={category._id} className="category-card">
              <div className="category-header">
                <div className="category-info" style={{display: 'flex', alignItems: 'center', gap: '1rem'}}>
                  <img 
                    src={category.image && typeof category.image === 'string' && category.image.trim().length > 0
                      ? (category.image.trim().startsWith('data:image/') 
                          ? category.image.trim() 
                          : convertGoogleDriveUrlForDisplay(category.image.trim()))
                      : getPlaceholderImage(60, 60, category.name)}
                    alt={category.name}
                    style={{
                      width: '60px',
                      height: '60px',
                      objectFit: 'cover',
                      borderRadius: '4px',
                      border: '1px solid #ddd',
                      flexShrink: 0
                    }}
                    onError={(e) => {
                      // Prevent infinite retry loops
                      if (e.target.dataset.retryCount) {
                        const retryCount = parseInt(e.target.dataset.retryCount, 10);
                        if (retryCount >= 2) {
                          // Stop retrying after 2 attempts, use placeholder
                          e.target.src = getPlaceholderImage(60, 60, category.name);
                          e.target.onerror = null;
                          return;
                        }
                        e.target.dataset.retryCount = (retryCount + 1).toString();
                      } else {
                        e.target.dataset.retryCount = '1';
                      }

                      const originalSrc = e.target.src;
                      
                      // Skip base64 and placeholder images
                      if (originalSrc.startsWith('data:image/')) {
                        e.target.src = getPlaceholderImage(60, 60, category.name);
                        e.target.onerror = null;
                        return;
                      }

                      // Skip problematic Google URLs
                      if (originalSrc.includes('lh3.googleusercontent.com') || 
                          originalSrc.includes('drive.google.com/uc?export=download')) {
                        e.target.src = getPlaceholderImage(60, 60, category.name);
                        e.target.onerror = null;
                        return;
                      }

                      // Try alternative Google Drive formats if first attempt fails
                      if (originalSrc.includes('drive.google.com')) {
                        // Extract file ID
                        const fileIdMatch = originalSrc.match(/[?&]id=([a-zA-Z0-9_-]+)/) || 
                                          originalSrc.match(/\/file\/d\/([a-zA-Z0-9_-]+)/) ||
                                          originalSrc.match(/\/d\/([a-zA-Z0-9_-]+)/);
                        
                        if (fileIdMatch && fileIdMatch[1]) {
                          const fileId = fileIdMatch[1];
                          
                          // Try thumbnail method (more reliable)
                          if (!originalSrc.includes('thumbnail')) {
                            e.target.src = `https://drive.google.com/thumbnail?id=${fileId}&sz=w200`;
                            return;
                          }
                        }
                      }
                      
                      // Final fallback: use placeholder and stop retrying
                      e.target.src = getPlaceholderImage(60, 60, category.name);
                      e.target.onerror = null;
                    }}
                  />
                  <div>
                    <h3>{category.name}</h3>
                    <span className="category-slug">/{category.slug}</span>
                  </div>
                </div>
                <div className="category-actions">
                  <button 
                    className="btn-edit"
                    onClick={() => handleEditCategory(category)}
                  >
                    Edit
                  </button>
                  <button 
                    className="btn-delete"
                    onClick={() => handleDeleteCategory(category._id)}
                  >
                    Delete
                  </button>
                </div>
              </div>

              <div className="subcategories-section">
                <div className="subcategories-header">
                  <h4>Subcategories ({category.subcategories?.length || 0})</h4>
                  <button 
                    className="btn-add-subcategory"
                    onClick={() => {
                      setSelectedCategory(category);
                      setSubcategoryForm({ name: "", slug: "" });
                      setSubcategorySlugManuallyEdited(false);
                      setShowSubcategoryModal(true);
                    }}
                  >
                    + Add Subcategory
                  </button>
                </div>

                {category.subcategories && category.subcategories.length > 0 ? (
                  <div className="subcategories-list">
                    {category.subcategories.map((subcat, index) => (
                      <div key={index} className="subcategory-item">
                        <div className="subcategory-info">
                          <span className="subcategory-name">{subcat.name}</span>
                          <span className="subcategory-slug">/{subcat.slug}</span>
                        </div>
                        <button 
                          className="btn-delete-small"
                          onClick={() => handleDeleteSubcategory(category._id, index)}
                        >
                          🗑️
                        </button>
                      </div>
                    ))}
                  </div>
                ) : (
                  <p className="no-subcategories">No subcategories yet</p>
                )}
              </div>
            </div>
          ))
        )}
      </div>

      {/* Category Modal */}
      {showCategoryModal && (
        <div className="modal-overlay" onClick={() => {
          setShowCategoryModal(false);
          setEditingCategory(null);
          setFormData({ name: "", slug: "", image: "" });
          setSlugManuallyEdited(false);
          setImageUploadProgress('');
        }}>
          <div className="modal-content" onClick={(e) => e.stopPropagation()}>
            <div className="modal-header">
              <h2>{editingCategory ? 'Edit Category' : 'Add New Category'}</h2>
              <button 
                className="modal-close"
                onClick={() => {
                  setShowCategoryModal(false);
                  setEditingCategory(null);
                  setFormData({ name: "", slug: "", image: "" });
                  setSlugManuallyEdited(false);
                  setImageUploadProgress('');
                }}
              >
                ×
              </button>
            </div>

            <form onSubmit={editingCategory ? handleUpdateCategory : handleCreateCategory} className="category-form">
              <div className="form-group">
                <label>Category Name *</label>
                <input
                  type="text"
                  name="name"
                  value={formData.name}
                  onChange={handleInputChange}
                  required
                  placeholder="e.g., Fruits & Vegetables"
                />
              </div>

              <div className="form-group">
                <label>Slug</label>
                <input
                  type="text"
                  name="slug"
                  value={formData.slug}
                  onChange={handleInputChange}
                  placeholder="Auto-generated from name"
                />
              </div>

              <div className="form-group">
                <label>Category Image</label>
                <div style={{marginBottom: '0.5rem'}}>
                  <input
                    type="file"
                    accept="image/*"
                    onChange={handleImageUpload}
                    disabled={uploadingImage}
                    style={{
                      width: '100%',
                      padding: '0.5rem',
                      border: '1px solid #ddd',
                      borderRadius: '4px',
                      backgroundColor: 'white',
                      cursor: uploadingImage ? 'not-allowed' : 'pointer'
                    }}
                  />
                </div>
                {uploadingImage && imageUploadProgress && (
                  <div style={{
                    padding: '0.5rem',
                    backgroundColor: '#e3f2fd',
                    border: '1px solid #2196f3',
                    borderRadius: '4px',
                    color: '#1976d2',
                    fontSize: '0.85rem',
                    marginBottom: '0.5rem'
                  }}>
                    {imageUploadProgress}
                  </div>
                )}
                {formData.image && (
                  <div style={{marginTop: '0.5rem'}}>
                    <img 
                      src={formData.image.startsWith('data:image/') 
                        ? formData.image 
                        : convertGoogleDriveUrlForDisplay(formData.image)} 
                      alt="Category preview"
                      style={{
                        width: '100px',
                        height: '100px',
                        objectFit: 'cover',
                        borderRadius: '4px',
                        border: '1px solid #ddd',
                        display: 'block'
                      }}
                      onError={(e) => {
                        console.error('Image preview error:', {
                          src: e.target.src,
                          formDataImage: formData.image ? formData.image.substring(0, 100) : 'none'
                        });
                        e.target.style.display = 'none';
                      }}
                      onLoad={() => {
                        console.log('Image preview loaded successfully');
                      }}
                    />
                    <button
                      type="button"
                      onClick={() => {
                        console.log('Removing image from formData');
                        setFormData(prev => ({ ...prev, image: "" }))
                      }}
                      style={{
                        marginTop: '0.5rem',
                        padding: '0.25rem 0.5rem',
                        background: '#e74c3c',
                        color: 'white',
                        border: 'none',
                        borderRadius: '4px',
                        cursor: 'pointer',
                        fontSize: '0.85rem'
                      }}
                    >
                      Remove Image
                    </button>
                  </div>
                )}
                <small style={{color: '#666', fontSize: '0.85rem', display: 'block', marginTop: '0.5rem'}}>
                  Upload an image for this category (Max 200KB). This will be displayed on the home page.
                </small>
              </div>

              <div className="form-actions">
                <button 
                  type="button" 
                  onClick={() => {
                    setShowCategoryModal(false);
                    setEditingCategory(null);
                    setFormData({ name: "", slug: "", image: "" });
                    setSlugManuallyEdited(false);
                    setImageUploadProgress('');
                  }} 
                  className="btn-cancel"
                >
                  Cancel
                </button>
                <button type="submit" className="btn-submit" disabled={uploadingImage}>
                  {editingCategory ? 'Update Category' : 'Create Category'}
                </button>
              </div>
            </form>
          </div>
        </div>
      )}

      {/* Subcategory Modal */}
      {showSubcategoryModal && (
        <div className="modal-overlay" onClick={() => {
          setShowSubcategoryModal(false);
          setSelectedCategory(null);
          setSubcategoryForm({ name: "", slug: "" });
          setSubcategorySlugManuallyEdited(false);
        }}>
          <div className="modal-content" onClick={(e) => e.stopPropagation()}>
            <div className="modal-header">
              <h2>Add New Subcategory</h2>
              <button 
                className="modal-close"
                onClick={() => {
                  setShowSubcategoryModal(false);
                  setSelectedCategory(null);
                  setSubcategoryForm({ name: "", slug: "" });
                  setSubcategorySlugManuallyEdited(false);
                }}
              >
                ×
              </button>
            </div>

            <form onSubmit={handleAddSubcategory} className="category-form">
              {!selectedCategory && (
                <div className="form-group">
                  <label>Select Category *</label>
                  <select
                    value={selectedCategory?._id || ""}
                    onChange={(e) => {
                      const category = categories.find(cat => cat._id === e.target.value);
                      setSelectedCategory(category);
                    }}
                    required
                  >
                    <option value="">Select a category</option>
                    {categories.map(cat => (
                      <option key={cat._id} value={cat._id}>
                        {cat.name}
                      </option>
                    ))}
                  </select>
                </div>
              )}

              {selectedCategory && (
                <div className="form-group">
                  <label>Parent Category</label>
                  <input
                    type="text"
                    value={selectedCategory.name}
                    disabled
                    className="disabled-input"
                  />
                </div>
              )}

              <div className="form-group">
                <label>Subcategory Name *</label>
                <input
                  type="text"
                  name="name"
                  value={subcategoryForm.name}
                  onChange={handleSubcategoryInputChange}
                  required
                  placeholder="e.g., Fresh Fruits"
                />
              </div>

              <div className="form-group">
                <label>Slug</label>
                <input
                  type="text"
                  name="slug"
                  value={subcategoryForm.slug}
                  onChange={handleSubcategoryInputChange}
                  placeholder="Auto-generated from name"
                />
              </div>

              <div className="form-actions">
                <button 
                  type="button" 
                  onClick={() => {
                    setShowSubcategoryModal(false);
                    setSelectedCategory(null);
                    setSubcategoryForm({ name: "", slug: "" });
                    setSubcategorySlugManuallyEdited(false);
                  }} 
                  className="btn-cancel"
                >
                  Cancel
                </button>
                <button 
                  type="submit" 
                  className="btn-submit"
                  disabled={!selectedCategory}
                >
                  Add Subcategory
                </button>
              </div>
            </form>
          </div>
        </div>
      )}
    </div>
  );
}

